import { Button, Modal, Title } from "@mantine/core";
import * as Sentry from "@sentry/browser";
import { type ReactNode } from "react";
import { generatePath, Link } from "react-router";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { ROUTES } from "../../../../routes";
import type {
  SiteEnergyWizardPhases,
  SiteWizardPhases,
  WizardDetail,
  WizardMissingData
} from "../../../../utils/backend-types";
import {
  MissingDataComponentName,
  MissingDataTypes
} from "../../../../utils/backend-types";
import { NoDataMessage } from "../../../BuildingBlocks/NoDataMessage/NoDataMessage";
import { GENERATOR_EDIT_TABS } from "../../../ObjectModals/GeneratorModal/GeneratorModal.constants";
import { COMPONENTS_PANEL_ID } from "../../../StructureView/ComponentsPanel/ComponentsPanel";
import "./onboardingMailPage.scss";
import { MISSING_DATA_DESCRIPTIONS } from "./utils/missingDataDescriptions";

type OnboardingMailPageProps = {
  projectId: string;
  details?: WizardDetail;
  onClose: () => void;
  phaseKey: keyof SiteWizardPhases | keyof SiteEnergyWizardPhases;
  variantId?: number;
  siteId: number;
};

function getLinkNameFromComponentName(componentName: MissingDataComponentName) {
  switch (componentName) {
    case MissingDataComponentName.PERSON:
      return "../unternehmen";
    case MissingDataComponentName.GENERATOR:
      return "../erzeuger";
    case MissingDataComponentName.METER:
      return "../zaehler";
    case MissingDataComponentName.CONNECTION:
      return "../netzverknuepfungspunkte";
    case MissingDataComponentName.CONSUMER:
      return "../verbraucher";
    case MissingDataComponentName.MARKET_LOCATION:
      return "../market-locations";
    case MissingDataComponentName.METERING_LOCATION:
      return "../metering-locations";
    default:
      return "fehler";
  }
}

function getTabNameFromDataType(dataType: MissingDataTypes) {
  switch (dataType) {
    case MissingDataTypes.DEFAULT:
      return "general";
    case MissingDataTypes.METERING_CONCEPT:
      return "messkonzept";
    case MissingDataTypes.ANLAGE:
      return `?tab=${GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA}`;
    case MissingDataTypes.REGULATORY:
      return "regulatorische-daten";
    case MissingDataTypes.TAX:
      return "daten-steuerformulare";
    case MissingDataTypes.INVOICE:
      return "stromlieferung-und-abrechnung";
    case MissingDataTypes.MEASUREMENT:
      return "messwerte";
    case MissingDataTypes.EEG:
      return `?tab=${GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA}`;
    case MissingDataTypes.KWK:
      return `?tab=${GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA}`;
    default:
      return "fehler";
  }
}

function wizardLinkBuilder(
  projectId: string,
  componentName: MissingDataComponentName,
  id: number,
  missingDataType: MissingDataTypes,
  variantId?: number
) {
  if (componentName === MissingDataComponentName.SITE) {
    return "./../../?site=" + id;
  }

  const contractsPage = generatePath(ROUTES.mieterstrom.contracts, {
    projectId
  });
  if (componentName === MissingDataComponentName.DELIVERY) {
    return `./../../../lieferungen-uebersicht/vertraege/lieferung/${id}/`;
  }
  if (componentName === MissingDataComponentName.MIETERSTROM_CONTRACT) {
    return `${contractsPage}/${id}/`;
  }

  if (componentName === MissingDataComponentName.GENERATION_SHARE) {
    return `./../erzeugungsanteile#${COMPONENTS_PANEL_ID}`;
  }

  if (
    componentName === MissingDataComponentName.GENERATOR &&
    missingDataType === MissingDataTypes.CONTRACT
  ) {
    return `${ROUTES.vermarktung.entry}?variantId=${variantId}`;
  }

  const linkName = getLinkNameFromComponentName(componentName);
  const tabName = getTabNameFromDataType(missingDataType);
  if (tabName === "fehler" || linkName === "fehler") return "fehler";
  return `${linkName}/${id}/${tabName}`;
}

export function groupMissingData(
  missingData: WizardMissingData[]
): Record<string, WizardMissingData[]> {
  const groupedData: Record<string, WizardMissingData[]> = {};

  missingData.forEach((data) => {
    const { componentName, missingDataType } = data;
    if (missingDataType === MissingDataTypes.POWER_OF_ATTORNEY_DATA) {
      return;
    }
    const key = `${componentName}-${missingDataType}`;

    if (!groupedData[key]) {
      groupedData[key] = [];
    }

    groupedData[key].push(data);
  });

  return groupedData;
}

function getDescriptionForComponentAndMissingDataType(
  componentName: MissingDataComponentName,
  missingDataType: MissingDataTypes
) {
  switch (componentName) {
    case MissingDataComponentName.PERSON:
      switch (missingDataType) {
        case MissingDataTypes.DEFAULT:
          return MISSING_DATA_DESCRIPTIONS.configuration.companies;
        case MissingDataTypes.REGULATORY:
          return MISSING_DATA_DESCRIPTIONS.regulatory.regulatoryData;
        case MissingDataTypes.TAX:
          return MISSING_DATA_DESCRIPTIONS.regulatory.taxData;
        case MissingDataTypes.INVOICE:
          return MISSING_DATA_DESCRIPTIONS.regulatory.invoiceData;
        default:
          break;
      }
      break;
    case MissingDataComponentName.GENERATOR:
      switch (missingDataType) {
        case MissingDataTypes.DEFAULT:
          return MISSING_DATA_DESCRIPTIONS.regulatory.generalGenerators;
        case MissingDataTypes.ANLAGE:
          return MISSING_DATA_DESCRIPTIONS.regulatory.regulatoryGenerators;
        case MissingDataTypes.EEG:
          return MISSING_DATA_DESCRIPTIONS.regulatory.eeg;
        case MissingDataTypes.KWK:
          return MISSING_DATA_DESCRIPTIONS.regulatory.kwk;
        case MissingDataTypes.CONTRACT:
          return MISSING_DATA_DESCRIPTIONS.generator.contract;
        default:
          break;
      }
      break;
    case MissingDataComponentName.GENERATION_SHARE:
      if (missingDataType === MissingDataTypes.DEFAULT) {
        return MISSING_DATA_DESCRIPTIONS.regulatory.generationShares;
      }
      break;
    case MissingDataComponentName.METER:
      switch (missingDataType) {
        case MissingDataTypes.METERING_CONCEPT:
          return MISSING_DATA_DESCRIPTIONS.meteringConcept.meterMeteringConcept;
        case MissingDataTypes.DEFAULT:
          return MISSING_DATA_DESCRIPTIONS.vollmacht.meters;
        case MissingDataTypes.MEASUREMENT:
          return MISSING_DATA_DESCRIPTIONS.historischeMessdaten.meters;
        default:
          break;
      }
      break;
    case MissingDataComponentName.CONNECTION:
      if (missingDataType === MissingDataTypes.DEFAULT) {
        return MISSING_DATA_DESCRIPTIONS.configuration.connections;
      }
      break;
    case MissingDataComponentName.SITE:
      if (missingDataType === MissingDataTypes.DEFAULT) {
        return MISSING_DATA_DESCRIPTIONS.invoice.site;
      }
      break;
    case MissingDataComponentName.DELIVERY:
      if (missingDataType === MissingDataTypes.DEFAULT) {
        return MISSING_DATA_DESCRIPTIONS.invoice.delivery;
      }
      break;
    case MissingDataComponentName.CONSUMER:
      if (missingDataType === MissingDataTypes.REGULATORY) {
        return MISSING_DATA_DESCRIPTIONS.regulatory.regulatoryConsumers;
      }
      break;
    case MissingDataComponentName.MARKET_LOCATION:
      if (missingDataType === MissingDataTypes.DEFAULT) {
        return MISSING_DATA_DESCRIPTIONS.vollmacht.marketLocations;
      }
      break;
    case MissingDataComponentName.METERING_LOCATION:
      if (missingDataType === MissingDataTypes.DEFAULT) {
        return MISSING_DATA_DESCRIPTIONS.vollmacht.meteringLocations;
      }
      break;
    default:
      return "Für die folgenden Komponenten fehlen Daten: ";
  }
}

function removeComponentTypeFromCategory(
  category: string
): MissingDataTypes | null {
  const [, missingDataType] = category.split("-");
  if (
    missingDataType &&
    Object.values(MissingDataTypes).includes(
      missingDataType as MissingDataTypes
    )
  ) {
    return missingDataType as MissingDataTypes;
  }
  return null;
}

function getTabNameFromCategory(category: string) {
  const missingDataType = removeComponentTypeFromCategory(category);
  const tabName = missingDataType
    ? getTabNameFromDataType(missingDataType)
    : null;
  if (tabName) {
    return tabName;
  } else {
    return "general";
  }
}

function getMissingFieldsForId(
  data: Record<string, WizardMissingData[]>,
  targetId: number
) {
  const missingFieldsForId = {};
  for (const category in data) {
    for (const item of data[category]) {
      if (item.id === targetId) {
        const categoryName = getTabNameFromCategory(category);
        missingFieldsForId[categoryName] = [
          ...(missingFieldsForId[categoryName] ?? []),
          ...item.missingFields
        ];
      }
    }
  }
  return missingFieldsForId;
}

interface RenderGroupedMissingDataParams {
  projectId: string;
  groupedData: Record<string, WizardMissingData[]>;
  exclusiveOrFields?: Array<Array<string>>;
  phaseKey?: keyof SiteWizardPhases | keyof SiteEnergyWizardPhases;
  variantId?: number;
  siteId?: number;
}

export function renderGroupedMissingData({
  projectId,
  groupedData,
  exclusiveOrFields = [],
  phaseKey,
  variantId,
  siteId
}: RenderGroupedMissingDataParams) {
  const renderedData: Array<ReactNode> = [];
  for (const key in groupedData) {
    const [componentName, missingDataType] = key.split("-");
    const description = getDescriptionForComponentAndMissingDataType(
      componentName as MissingDataComponentName,
      missingDataType as MissingDataTypes
    );

    renderedData.push(
      <div key={key}>
        <Title mb="sm" order={5}>
          {description}
        </Title>
        <div className="missing-data-link-container">
          {groupedData[key].map((data, index) => {
            const link = wizardLinkBuilder(
              projectId,
              data.componentName,
              data.id,
              data.missingDataType,
              variantId
            );
            if (link === "fehler") {
              Sentry.captureMessage(
                `Unsupported optiguide missing data component or type: ${data.componentName} - ${data.missingDataType}`
              );
              return null;
            }
            return (
              <Link
                key={index}
                state={{
                  missingFields: getMissingFieldsForId(groupedData, data.id),
                  exclusiveOrFields: exclusiveOrFields,
                  ...(phaseKey !== undefined && {
                    highlightMissingFieldsFromPhase: phaseKey
                  }),
                  projectId,
                  siteId
                }}
                to={link}
              >
                {data.name}
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return renderedData;
}

function OnboardingMailPage({
  details,
  onClose,
  phaseKey,
  projectId,
  variantId,
  siteId
}: OnboardingMailPageProps) {
  const headerContent = details ? details.name : "Onboarding Fehler";
  const groupedMissingData = details
    ? groupMissingData(details.missingData)
    : null;
  const hasMissingData =
    groupedMissingData !== null && Object.keys(groupedMissingData).length > 0;
  const exclusiveOrFields = details ? details.exclusiveOrFields : [];

  return (
    <Modal opened size="lg" title={headerContent} onClose={onClose}>
      {hasMissingData ? (
        renderGroupedMissingData({
          projectId,
          groupedData: groupedMissingData,
          exclusiveOrFields,
          phaseKey,
          variantId,
          siteId
        })
      ) : (
        <NoDataMessage message="Keine Onboarding Daten verfügbar" />
      )}
      <ModalFooter>
        <Button onClick={onClose}>Fertig</Button>
      </ModalFooter>
    </Modal>
  );
}

export { OnboardingMailPage };
